import { gql } from '@apollo/client';
import { S3 } from 'aws-sdk';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from 'aws-exports';
import * as Sentry from '@sentry/react';

import { apolloClient } from 'core/apollo-client';

const GET_UPLOAD_URL = gql`
  query GetUploadUrl($webUserId: ID!, $fileName: String!, $mimeType: String!, $taskName: String, $offerId: ID) {
    getUploadUrl(webUserId: $webUserId, fileName: $fileName, mimeType: $mimeType, taskName: $taskName, offerId: $offerId) {
      url
      key
    }
  }
`;

const TAG_DOCUMENT = gql`
  query TagDocument($key: String!, $taskName: String!, $offerId: ID, $uploadedBy: String, $customer: String) {
    tagDocument(key: $key, taskName: $taskName, offerId: $offerId, uploadedBy: $uploadedBy, customer: $customer) {
      success
    }
  }
`;

const GET_DOCS = gql`
  query GetDocs($webUserId: ID!) {
    getDocs(webUserId: $webUserId) {
      internal {
        path
        lastModified
        displayName
      }
      regular {
        path
        lastModified
        displayName
      }
    }
  }
`;

const SET_DOCUMENT_LABEL = gql`
  mutation SetDocumentLabel($webUserId: ID, $key: String!, $label: String) {
    setDocumentLabel(webUserId: $webUserId, key: $key, label: $label) {
      success
      key
      displayName
    }
  }
`;

/**
 * Get upload URL and upload file to S3
 * @param {string} webUserId - The web user ID
 * @param {File} file - The file to upload
 * @param {Object} options - Optional parameters
 * @param {string} options.taskName - Optional task name for task documents
 * @param {string} options.offerId - Optional offer ID for task documents
 * @param {string} options.customer - Optional customer name for customer-specific tasks
 */
export const uploadOfferDocument = async (webUserId, file, options = {}) => {
  try {
    const { taskName, offerId, uploadedBy, customer } = options;

    // Step 1: Get upload URL
    const { data: uploadUrlData } = await apolloClient.query({
      query: GET_UPLOAD_URL,
      variables: {
        webUserId,
        fileName: file.name,
        mimeType: file.type,
        taskName,
        offerId
      }
    });

    const { url: uploadUrl, key } = uploadUrlData.getUploadUrl;

    // Step 2: Upload file to S3
    await uploadFileToS3(uploadUrl, file);

    // Step 3: Tag the document if taskName is provided
    if (taskName && key) {
      try {
        await apolloClient.query({
          query: TAG_DOCUMENT,
          variables: {
            key,
            taskName,
            offerId,
            uploadedBy: uploadedBy || 'Staff',
            customer
          },
          fetchPolicy: 'no-cache'
        });
      } catch (tagError) {
        Sentry.captureException(tagError, { tags: { taskName }, key, offerId, uploadedBy });
      }
    }

    // Step 4: Return document info
    return {
      id: `offer/${webUserId}/${file.name}`,
      fileName: file.name,
      uploadedAt: new Date().toISOString(),
      taskName,
      offerId
    };
  } catch (error) {
    console.error('Error uploading offer document:', error);
    // Preserve the original error message
    throw error;
  }
};

/**
 * Fetch documents for an offer by webUserId
 * @param {string} webUserId - The web user ID
 * @returns {Promise<Object>} - Documents object with internal and regular arrays
 */
export const fetchOfferDocuments = async (webUserId) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_DOCS,
      variables: { webUserId },
      fetchPolicy: 'no-cache'
    });

    return data?.getDocs || { internal: [], regular: [] };
  } catch (error) {
    console.error('Error fetching offer documents:', error);
    throw error;
  }
};

/**
 * Set (or clear) the editable display label on an offer document.
 * @param {string} webUserId - The web user ID that owns the document
 * @param {string} key - The S3 key of the document (its path)
 * @param {string} label - The new display label; blank clears it
 * @returns {Promise<Object>} - { success, key, displayName }
 */
export const setOfferDocumentLabel = async (webUserId, key, label) => {
  const { data } = await apolloClient.mutate({
    mutation: SET_DOCUMENT_LABEL,
    variables: { webUserId, key, label }
  });
  return data?.setDocumentLabel;
};

/**
 * Get a short-lived signed URL for viewing a document (returns the URL string).
 * Used by the image-preview gallery, which needs the URL for an <img> src rather
 * than opening a tab.
 * @param {string} path - The document S3 path/key
 * @param {number} secondsBeforeExpire - URL expiration time in seconds (default: 24 hours)
 * @returns {Promise<string>} - The signed URL
 */
export const getOfferDocumentSignedUrl = async (path, secondsBeforeExpire = 86400) => {
  const { credentials } = await fetchAuthSession();
  const s3Client = new S3({
    region: config.Storage.region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    }
  });
  return s3Client.getSignedUrl('getObject', {
    Key: path,
    Bucket: config.Storage.bucket,
    Expires: secondsBeforeExpire
  });
};

/**
 * Download a document by opening it in a new window with a signed URL
 * @param {string} path - The document S3 path/key
 * @param {number} secondsBeforeExpire - URL expiration time in seconds (default: 24 hours)
 */
export const downloadOfferDocument = async (path, secondsBeforeExpire = 86400) => {
  try {
    const url = await getOfferDocumentSignedUrl(path, secondsBeforeExpire);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error downloading document:', error);
    throw error;
  }
};

/**
 * Upload file to S3 using presigned URL
 * Note: Tags are already included in the presigned URL signature, not sent as headers
 */
const uploadFileToS3 = (uploadUrl, file) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};
